import { z } from 'zod';

export const DEFAULT_CONTENT_INTELLIGENCE_GIT_WORK_LIMIT = 50;
export const MAX_CONTENT_INTELLIGENCE_GIT_WORK_LIMIT = 100;

export const contentIntelligenceGitWorkVerdictSchema = z.enum([
  'aligned',
  'mismatch',
  'weak_signal',
  'insufficient_evidence',
]);

export const contentIntelligenceGitWorkFactorsSchema = z
  .object({
    statusCategory: z.enum(['to_do', 'in_progress', 'done', 'unknown']),
    totalActivities: z.number().int().nonnegative(),
    commitCount: z.number().int().nonnegative(),
    pullRequestCount: z.number().int().nonnegative(),
    mergedPullRequestCount: z.number().int().nonnegative(),
    openPullRequestCount: z.number().int().nonnegative(),
    reviewCount: z.number().int().nonnegative(),
    descriptiveEvidenceCount: z.number().int().nonnegative(),
    outsideWindowActivityCount: z.number().int().nonnegative(),
    firstActivityAt: z.iso.datetime().nullable(),
    latestActivityAt: z.iso.datetime().nullable(),
    evidenceTruncated: z.boolean(),
  })
  .strict();

export const contentIntelligenceGitWorkEvidenceSchema = z
  .object({
    id: z.string().min(1),
    type: z.enum(['pull_request_event', 'pull_request_review', 'commit']),
    repository: z.string().min(1),
    pullRequestNumber: z.number().int().positive(),
    occurredAt: z.iso.datetime(),
    url: z.url(),
    summary: z.string().min(1).optional(),
  })
  .strict();

export const contentIntelligenceGitWorkQuerySchema = z.object({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_CONTENT_INTELLIGENCE_GIT_WORK_LIMIT)
    .default(DEFAULT_CONTENT_INTELLIGENCE_GIT_WORK_LIMIT),
});

const gitWorkAssessmentFields = {
  assessmentId: z.string().min(1),
  itemId: z.number().int().positive(),
  loopId: z.number().int().positive(),
  externalKey: z.string().min(1),
  verdict: contentIntelligenceGitWorkVerdictSchema,
  score: z.number().min(0).max(1),
  confidence: z.number().min(0).max(1),
  rationale: z.string().trim().min(1).max(1_000),
  suggestedAction: z.string().trim().min(1).max(500).nullable(),
  factors: contentIntelligenceGitWorkFactorsSchema,
  evidence: z.array(contentIntelligenceGitWorkEvidenceSchema).max(100),
  caveats: z.array(z.string().trim().min(1).max(500)).max(10),
  pipelineVersion: z.string().min(1),
  promptVersion: z.string().min(1),
  inputVersion: z.string().min(1),
  modelName: z.string().min(1),
  itemUpdatedAt: z.iso.datetime().nullable(),
  evaluatedAt: z.iso.datetime(),
};

export const contentIntelligenceGitWorkAssessmentSchema = z.object({
  ...gitWorkAssessmentFields,
  status: z.literal('assessed'),
});

export const contentIntelligenceGitWorkResponseSchema = z
  .object({
    loopId: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    truncated: z.boolean(),
    items: z
      .array(contentIntelligenceGitWorkAssessmentSchema)
      .max(MAX_CONTENT_INTELLIGENCE_GIT_WORK_LIMIT),
    dataSource: z.literal('sir-git-work'),
  })
  .superRefine((response, context) => {
    validateCollection(response, context, 'assessment');
  });

const validateCollection = (
  response: {
    loopId: number;
    total: number;
    returned: number;
    truncated: boolean;
    items: Array<{ loopId: number }>;
  },
  context: z.RefinementCtx,
  label: string,
): void => {
  if (response.returned !== response.items.length) {
    context.addIssue({
      code: 'custom',
      message: `Returned count must equal the number of ${label} items`,
      path: ['returned'],
    });
  }
  if (response.total < response.returned) {
    context.addIssue({
      code: 'custom',
      message: 'Total count cannot be lower than the returned count',
      path: ['total'],
    });
  }
  const shouldBeTruncated = response.total > response.returned;
  if (response.truncated !== shouldBeTruncated) {
    context.addIssue({
      code: 'custom',
      message: `Truncated must report whether more ${label} items are available`,
      path: ['truncated'],
    });
  }
  for (const [index, item] of response.items.entries()) {
    if (item.loopId !== response.loopId) {
      context.addIssue({
        code: 'custom',
        message: `${label} loop must match the requested loop`,
        path: ['items', index, 'loopId'],
      });
    }
  }
};

export type ContentIntelligenceGitWorkVerdict = z.infer<
  typeof contentIntelligenceGitWorkVerdictSchema
>;
export type ContentIntelligenceGitWorkFactors = z.infer<
  typeof contentIntelligenceGitWorkFactorsSchema
>;
export type ContentIntelligenceGitWorkAssessment = z.infer<
  typeof contentIntelligenceGitWorkAssessmentSchema
>;
export type ContentIntelligenceGitWorkResponse = z.infer<
  typeof contentIntelligenceGitWorkResponseSchema
>;
